import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const processPriority = p => {
    if (p.dataicon.startsWith('glyphicon')) {
        switch (p.dataicon) {
            case 'glyphicon-info-sign':
                p.dataicon = 'fa-info-circle';
                break;
            case 'glyphicon-arrow-up':
                p.dataicon = 'fa-arrow-up';
                break;
            case 'glyphicon-arrow-down':
                p.dataicon = 'fa-arrow-down';
                break;
            case 'glyphicon-warning-sign':
                p.dataicon = 'fa-exclamation-triangle';
                break;
            default:
                throw new Error("Unknown glyphicon " + p.dataicon);
        }
    }
    if (!p.color) {
        switch (p.value) {
            case 1:
                p.color = '#f2dede';
                break;
            case 2:
                p.color = '#fcf8e3';
                break;
            case 3:
                p.color = '#dff0d8';
                break;
            case 4:
                p.color = '#d9edf7';
                break;
            case 5:
                p.color = '#e2e2e2';
                break;
            case 6:
                p.color = '#f2d9f7';
                break;
            default:
                break;
        }
    }
    return p;
}

const prioritySlice = createSlice({
    name: 'priorities',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload.map(p => processPriority(p));
        },
        addOrUpdate: (state, action) => {
            for (const p of ensureArray(action.payload).map(processPriority)) {
                let idx = state.data.findIndex(p2 => p2.internalid === p.internalid);
                if (idx >= 0) {
                    state.data.splice(idx, 1, p);
                } else {
                    state.data.push(p);
                }
            }
        },
        remove: (state, action) => {
            for (const p of ensureArray(action.payload).map(processPriority)) {
                let idx = state.data.findIndex(p2 => p2.internalid === p.internalid);
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => prioritySlice.getInitialState());
    }
});

// #region network operations
const fetchPriorities = network => network.get('/api/v1/priorities', prioritySlice.actions.set);
export const createPriority = (network, priority) => network.post('/api/v1/priority', priority, prioritySlice.actions.addOrUpdate);
export const updatePriority = (network, priority) => network.put('/api/v1/priority', priority, prioritySlice.actions.addOrUpdate);
export const removePriority = (network, priority) => network.delete('/api/v1/priority/' + encodeURIComponent(priority.internalid), prioritySlice.actions.remove);
// #endregion

// #region selectors
const selectPriorities = state => state.priorities.data;

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectPriorityById = createSelector([selectPriorities, (priorities, id) => id], (priorities, id) => priorities.find(p => p.internalid === id));
const selectPriority = id => state => selectPriorityById(state, id);
// #endregion

// #region listeners
const listen = {
    priorityCreated: (priority, { dispatch }) => dispatch(prioritySlice.actions.addOrUpdate(priority)),
    priorityUpdated: (priority, { dispatch }) => dispatch(prioritySlice.actions.addOrUpdate(priority)),
    priorityRemoved: (priority, { dispatch }) => dispatch(prioritySlice.actions.remove(priority))
}
// #endregion

// #region permissions
export const canCreatePriority = (credentials, priority) => credentials.access.hasFull('administrator');
export const canUpdatePriority = (credentials, priority) => credentials.access.hasFull('administrator');
export const canRemovePriority = (credentials, priority) => credentials.access.hasFull('administrator');
// #endregion

export const [usePriorities, realizePriorities] = dataHookFactory(selectPriorities, { run: fetchPriorities, listen });
export const [usePriority] = dataHookFactory(selectPriority, initMapFactory(value => fetchPriorities, listen));

export const priorityIcon = p => {
    let icon = p?.dataicon;
    switch (icon) {
        case 'glyphicon-info-sign':
            icon = 'fa-info-circle';
            break;
        case 'glyphicon-arrow-up':
            icon = 'fa-arrow-up';
            break;
        case 'glyphicon-arrow-down':
            icon = 'fa-arrow-down';
            break;
        case 'glyphicon-warning-sign':
            icon = 'fa-exclamation-triangle';
            break;
        default:
            break;
    }
    if (!icon?.startsWith('fa-solid ') && !icon?.startsWith('fa-regular ')) {
      icon = 'fas ' + p?.dataicon;
    }
    return icon;
}

const reducerDetails = { name: prioritySlice.name, reducer: prioritySlice.reducer };
export default reducerDetails;